'use client';

import {
  Box,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import {
  Provider,
  SchemaEvolutionMode,
  SchemaRegistryAuthType,
  SubjectNamingStrategy,
} from '@repo/api-gw-sdk';

import { FormTextField } from '@/components/form/formTextField';
import { getCloudFieldConfig } from '@/components/form/validation/cloudFieldValidation';
import type { FieldValidation } from '@/components/form/validation/fieldValidation';
import { StepContainer } from '@/components/wizard/StepContainer';
import type { StepProps } from '@/components/wizard/StepProps';

import type { IngestionPipelineWizardState } from '../IngestionPipelineWizardState';
import { getStepperLabels } from '../kafkaHelpers';

const AUTH_TYPE_OPTIONS = [
  {
    value: SchemaRegistryAuthType.SCHEMA_REGISTRY_AUTH_TYPE_NONE,
    label: 'None',
  },
  {
    value: SchemaRegistryAuthType.SCHEMA_REGISTRY_AUTH_TYPE_BASIC,
    label: 'Basic',
  },
  {
    value: SchemaRegistryAuthType.SCHEMA_REGISTRY_AUTH_TYPE_BEARER,
    label: 'Bearer',
  },
  {
    value: SchemaRegistryAuthType.SCHEMA_REGISTRY_AUTH_TYPE_MTLS,
    label: 'mTLS',
  },
];

const SUBJECT_NAMING_STRATEGY_OPTIONS = [
  { value: '', label: '— None —' },
  {
    value: SubjectNamingStrategy.SUBJECT_NAMING_STRATEGY_TOPIC_NAME,
    label: 'Topic Name',
  },
  {
    value: SubjectNamingStrategy.SUBJECT_NAMING_STRATEGY_RECORD_NAME,
    label: 'Record Name',
  },
  {
    value: SubjectNamingStrategy.SUBJECT_NAMING_STRATEGY_TOPIC_RECORD_NAME,
    label: 'Topic Record Name',
  },
];

const EVOLUTION_MODE_OPTIONS = [
  { value: SchemaEvolutionMode.SCHEMA_EVOLUTION_MODE_STRICT, label: 'Strict' },
  {
    value: SchemaEvolutionMode.SCHEMA_EVOLUTION_MODE_BACKWARD_COMPATIBLE,
    label: 'Backward Compatible',
  },
  {
    value: SchemaEvolutionMode.SCHEMA_EVOLUTION_MODE_AUTO_UPDATE,
    label: 'Auto Update',
  },
];

const URL_VALIDATION: FieldValidation = {
  validateInput: (value: string) =>
    value.length === 0 || /^https?:\/\/.+/.test(value.trim()),
  errorMessage: () => 'Must be a valid URL starting with http:// or https://.',
};

const { validation: ARN_VALIDATION_OPTIONAL, placeholder: ARN_PLACEHOLDER } =
  getCloudFieldConfig(Provider.Aws, 'arn');

export function KafkaSchemaRegistryStep(
  props: StepProps<IngestionPipelineWizardState>
) {
  const {
    kafkaSchemaRegistryEnabled,
    kafkaSchemaRegistryUrl,
    kafkaSchemaRegistryAuthType,
    kafkaSchemaRegistryCredentialsSecretArn,
    kafkaSchemaRegistryTlsSecretArn,
    kafkaSubjectNamingStrategy,
    kafkaSchemaEvolutionMode,
  } = props.wizardState;

  const enabled = kafkaSchemaRegistryEnabled;

  const showCredentialsArn =
    kafkaSchemaRegistryAuthType ===
      SchemaRegistryAuthType.SCHEMA_REGISTRY_AUTH_TYPE_BASIC ||
    kafkaSchemaRegistryAuthType ===
      SchemaRegistryAuthType.SCHEMA_REGISTRY_AUTH_TYPE_BEARER;
  const showTlsArn =
    kafkaSchemaRegistryAuthType ===
    SchemaRegistryAuthType.SCHEMA_REGISTRY_AUTH_TYPE_MTLS;

  const canGoNext = () => {
    if (!enabled) return true;
    if (
      kafkaSchemaRegistryUrl.trim() === '' ||
      !URL_VALIDATION.validateInput(kafkaSchemaRegistryUrl)
    )
      return false;
    if (kafkaSchemaRegistryAuthType.trim() === '') return false;
    if (
      showCredentialsArn &&
      kafkaSchemaRegistryCredentialsSecretArn.trim() !== '' &&
      !ARN_VALIDATION_OPTIONAL.validateInput(
        kafkaSchemaRegistryCredentialsSecretArn
      )
    )
      return false;
    if (
      showTlsArn &&
      kafkaSchemaRegistryTlsSecretArn.trim() !== '' &&
      !ARN_VALIDATION_OPTIONAL.validateInput(kafkaSchemaRegistryTlsSecretArn)
    )
      return false;
    return true;
  };

  const handleToggle = (checked: boolean) => {
    props.setWizardState((s) => ({
      ...s,
      kafkaSchemaRegistryEnabled: checked,
      ...(!checked && {
        kafkaSchemaRegistryUrl: '',
        kafkaSchemaRegistryAuthType:
          SchemaRegistryAuthType.SCHEMA_REGISTRY_AUTH_TYPE_NONE,
        kafkaSchemaRegistryCredentialsSecretArn: '',
        kafkaSchemaRegistryTlsSecretArn: '',
        kafkaSubjectNamingStrategy: '',
        kafkaSchemaEvolutionMode:
          SchemaEvolutionMode.SCHEMA_EVOLUTION_MODE_BACKWARD_COMPATIBLE,
      }),
    }));
  };

  return (
    <StepContainer
      stepperIndex={3}
      stepperLabels={getStepperLabels(props.wizardState.sourceType)}
      canGoNext={canGoNext}
      onNextClick={async () => {
        props.setNextStep(props.currentStep.next?.[0]);
      }}
      onBackClick={props.back}
      sx={{ overflow: 'auto', paddingX: '0px', paddingY: 'min(60px, 4vw)' }}
    >
      <Stack gap='min(60px, 4vw)' divider={<Divider />}>
        {/* Schema Registry */}
        <Box paddingX='min(100px, 6vw)'>
          <Stack gap='24px' maxWidth='540px'>
            <Stack gap='16px'>
              <Stack gap='8px'>
                <Typography variant='h6'>Schema Registry</Typography>
                <Typography variant='body2' color='text.secondary'>
                  Configure the schema registry connection and authentication.
                </Typography>
              </Stack>
              <FormControlLabel
                control={
                  <Switch
                    checked={enabled}
                    onChange={(e) => handleToggle(e.target.checked)}
                    data-testid='kafka-schema-registry-toggle'
                  />
                }
                label='Enable Schema Registry'
                sx={{ gap: '8px' }}
              />
            </Stack>

            {enabled ? (
              <FormTextField
                title='Schema Registry URL'
                placeholder='e.g. https://schema-registry.example.com:8081'
                value={kafkaSchemaRegistryUrl}
                validation={URL_VALIDATION}
                onChange={(e) =>
                  props.setWizardState((s) => ({
                    ...s,
                    kafkaSchemaRegistryUrl: e.target.value,
                  }))
                }
                dataTestId='kafka-schema-registry-url-input'
              />
            ) : (
              <FormControl fullWidth disabled>
                <FormLabel>
                  <Typography variant='subtitle1' color='inherit'>
                    Schema Registry URL
                  </Typography>
                </FormLabel>
                <TextField
                  size='medium'
                  disabled
                  value=''
                  placeholder='e.g. https://schema-registry.example.com:8081'
                  slotProps={{
                    htmlInput: {
                      'data-testid': 'kafka-schema-registry-url-input',
                    },
                  }}
                />
              </FormControl>
            )}

            <FormControl fullWidth disabled={!enabled}>
              <FormLabel>
                <Typography variant='subtitle1' color='inherit'>
                  Auth Type
                </Typography>
              </FormLabel>
              <Select
                size='small'
                disabled={!enabled}
                value={
                  enabled
                    ? kafkaSchemaRegistryAuthType
                    : SchemaRegistryAuthType.SCHEMA_REGISTRY_AUTH_TYPE_NONE
                }
                onChange={(e) => {
                  const val = Object.values(SchemaRegistryAuthType).find(
                    (v) => v === e.target.value
                  );
                  if (val) {
                    props.setWizardState((s) => ({
                      ...s,
                      kafkaSchemaRegistryAuthType: val,
                      kafkaSchemaRegistryCredentialsSecretArn: '',
                      kafkaSchemaRegistryTlsSecretArn: '',
                    }));
                  }
                }}
                data-testid='kafka-schema-registry-auth-type-select'
              >
                {AUTH_TYPE_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {enabled && showCredentialsArn && (
              <FormTextField
                title='Credentials Secret ARN'
                placeholder={ARN_PLACEHOLDER}
                value={kafkaSchemaRegistryCredentialsSecretArn}
                validation={ARN_VALIDATION_OPTIONAL}
                onChange={(e) =>
                  props.setWizardState((s) => ({
                    ...s,
                    kafkaSchemaRegistryCredentialsSecretArn: e.target.value,
                  }))
                }
                dataTestId='kafka-schema-registry-credentials-arn-input'
              />
            )}

            {enabled && showTlsArn && (
              <FormTextField
                title='TLS Secret ARN'
                placeholder={ARN_PLACEHOLDER}
                value={kafkaSchemaRegistryTlsSecretArn}
                validation={ARN_VALIDATION_OPTIONAL}
                onChange={(e) =>
                  props.setWizardState((s) => ({
                    ...s,
                    kafkaSchemaRegistryTlsSecretArn: e.target.value,
                  }))
                }
                dataTestId='kafka-schema-registry-tls-arn-input'
              />
            )}
          </Stack>
        </Box>

        {/* Schema Options */}
        <Box paddingX='min(100px, 6vw)'>
          <Stack gap='24px' maxWidth='540px'>
            <Stack gap='8px'>
              <Typography
                variant='h6'
                color={enabled ? 'text.primary' : 'text.disabled'}
              >
                Schema Options
              </Typography>
              <Typography
                variant='body2'
                color={enabled ? 'text.secondary' : 'text.disabled'}
              >
                Configure schema naming and evolution behavior.
              </Typography>
            </Stack>

            <FormControl fullWidth disabled={!enabled}>
              <FormLabel>
                <Typography variant='subtitle1' color='inherit'>
                  Subject Naming Strategy
                </Typography>
              </FormLabel>
              <Select
                size='small'
                disabled={!enabled}
                value={enabled ? kafkaSubjectNamingStrategy : ''}
                onChange={(e) =>
                  props.setWizardState((s) => ({
                    ...s,
                    kafkaSubjectNamingStrategy: e.target.value,
                  }))
                }
                data-testid='kafka-subject-naming-strategy-select'
              >
                {SUBJECT_NAMING_STRATEGY_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth disabled={!enabled}>
              <FormLabel>
                <Typography variant='subtitle1' color='inherit'>
                  Schema Evolution Mode
                </Typography>
              </FormLabel>
              <Select
                size='small'
                disabled={!enabled}
                value={
                  enabled
                    ? kafkaSchemaEvolutionMode
                    : SchemaEvolutionMode.SCHEMA_EVOLUTION_MODE_BACKWARD_COMPATIBLE
                }
                onChange={(e) => {
                  const val = Object.values(SchemaEvolutionMode).find(
                    (v) => v === e.target.value
                  );
                  if (val) {
                    props.setWizardState((s) => ({
                      ...s,
                      kafkaSchemaEvolutionMode: val,
                    }));
                  }
                }}
                data-testid='kafka-schema-evolution-mode-select'
              >
                {EVOLUTION_MODE_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </Box>
      </Stack>
    </StepContainer>
  );
}
